import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from 'components/ui/dialog';
import {
  AnomalyPlant,
  BehaviourDetail,
  BehaviourProfile,
  BehaviourSettings,
  SaveBehaviour,
} from './types';
import Statistics from './Statistics';
import BehaviourSettingsForm from './BehaviourSettingsForm';

export type BehaviourService = {
  list: () => Promise<AnomalyPlant[]>;
  get: (sensorId: string, preview?: BehaviourSettings) => Promise<BehaviourDetail>;
  save: (command: SaveBehaviour) => Promise<BehaviourProfile>;
};
export default function BehaviourPanel({
  open,
  onClose,
  service,
  initialSensorId,
}: {
  open: boolean;
  onClose: () => void;
  service: BehaviourService;
  initialSensorId?: string;
}) {
  const [plants, setPlants] = useState<AnomalyPlant[]>([]);
  const [selected, setSelected] = useState('');
  const [detail, setDetail] = useState<BehaviourDetail | null>(null);
  const [draft, setDraft] = useState<BehaviourSettings | null>(null);
  const [preview, setPreview] = useState<BehaviourDetail | null>(null);
  const [previewSettings, setPreviewSettings] = useState('');
  const [tab, setTab] = useState<'statistics' | 'settings'>('statistics');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [reloadList, setReloadList] = useState(0);
  const request = useRef(0);
  const dirty =
    !!detail && !!draft && JSON.stringify(draft) !== JSON.stringify(detail.profile.settings);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setSelected('');
    setDetail(null);
    setDraft(null);
    setPlants([]);
    setLoading(true);
    setError('');
    service
      .list()
      .then((items) => {
        if (cancelled) return;
        setPlants(items);
        const id = items.some((p) => p.sensorId === initialSensorId)
          ? initialSensorId!
          : items[0]?.sensorId;
        if (id) void load(id);
        else {
          setDetail(null);
          setLoading(false);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e.message);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
      // This counter intentionally invalidates the latest request, not a DOM ref.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      request.current++;
    };
    // A new selection is loaded explicitly so refreshes cannot erase a draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, service, reloadList]);
  useEffect(() => {
    const handler = (event: BeforeUnloadEvent) => {
      if (open && (dirty || saving)) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [open, dirty, saving]);
  function mayLeave() {
    return (
      !saving &&
      (!dirty || window.confirm('Der er ugemte forventninger. Vil du kassere ændringerne?'))
    );
  }
  async function load(id: string) {
    const generation = ++request.current;
    setSelected(id);
    setDetail(null);
    setDraft(null);
    setPreview(null);
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const data = await service.get(id);
      if (request.current !== generation) return;
      setDetail(data);
      setDraft(data.profile.settings);
    } catch (cause) {
      if (request.current === generation) setError((cause as Error).message);
    } finally {
      if (request.current === generation) setLoading(false);
    }
  }
  function validate() {
    if (!draft) return false;
    if (
      draft.schedule &&
      (!draft.schedule.weekdays.length || draft.schedule.startHour >= draft.schedule.endHour)
    ) {
      setError('Vælg mindst én driftsdag og en sluttid efter starttid.');
      return false;
    }
    return true;
  }
  async function calculate() {
    if (!draft || !detail || saving || !validate()) return;
    setSaving(true);
    setError('');
    try {
      setPreview(await service.get(selected, draft));
      setPreviewSettings(JSON.stringify(draft));
      setTab('statistics');
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setSaving(false);
    }
  }
  async function save() {
    if (!detail || !draft || saving || !validate()) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const profile = await service.save({
        sensorId: selected,
        expectedVersion: detail.profile.version,
        settings: draft,
      });
      setDetail({ ...detail, profile });
      setDraft(profile.settings);
      setPreview(null);
      setMessage(
        'Forventninger gemt. De bruges fra næste analyse på nye målinger. Tidligere hændelser bevares.'
      );
      const refreshed = await service.get(selected);
      setDetail(refreshed);
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setSaving(false);
    }
  }
  const shownPreview = preview && previewSettings === JSON.stringify(draft) ? preview : null;
  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!value && mayLeave()) onClose();
      }}
    >
      <DialogContent className="behaviour-dialog">
        <DialogHeader>
          <DialogTitle>Normal adfærd & statistik</DialogTitle>
          <DialogDescription>
            Alle aktive anlæg — også dem uden afvigelser. Se vand, binder og niveau, og tilpas hvad
            der er normalt.
          </DialogDescription>
        </DialogHeader>
        <div className="behaviour-content">
          <label className="anomaly-field-label" htmlFor="behaviour-plant">
            Anlæg
          </label>
          <select
            id="behaviour-plant"
            className="anomaly-input"
            disabled={saving}
            value={selected}
            onChange={(e) => {
              if (mayLeave()) void load(e.target.value);
            }}
          >
            {!plants.length && (
              <option value="">{loading ? 'Henter anlæg…' : 'Ingen aktive anlæg'}</option>
            )}
            {(['FLOW', 'GSM'] as const).map((family) => (
              <optgroup key={family} label={family === 'FLOW' ? 'Flow / niveau' : 'GSM anlæg'}>
                {plants
                  .filter((p) => p.deviceFamily === family)
                  .map((p) => (
                    <option key={p.sensorId} value={p.sensorId}>
                      {p.orgTitle} · {p.sensorTitle}
                    </option>
                  ))}
              </optgroup>
            ))}
          </select>
          <p className="anomaly-muted">
            {plants.filter((p) => p.deviceFamily === 'FLOW').length} flow-sensorer ·{' '}
            {plants.filter((p) => p.deviceFamily === 'GSM').length} GSM-anlæg
          </p>
          {error && (
            <div role="alert" className="anomaly-error">
              {error}{' '}
              <button
                type="button"
                onClick={() => {
                  if (mayLeave()) {
                    if (selected) void load(selected);
                    else setReloadList((value) => value + 1);
                  }
                }}
              >
                Genindlæs anlæg
              </button>
            </div>
          )}
          {message && (
            <p role="status" className="behaviour-success">
              {message}
            </p>
          )}
          {loading ? (
            <p role="status">Henter indstillinger og statistik…</p>
          ) : (
            detail &&
            draft && (
              <>
                <div className="behaviour-tabs" aria-label="Anlægsvisning">
                  <button
                    type="button"
                    aria-pressed={tab === 'statistics'}
                    onClick={() => setTab('statistics')}
                  >
                    Statistik
                  </button>
                  <button
                    type="button"
                    aria-pressed={tab === 'settings'}
                    onClick={() => setTab('settings')}
                  >
                    Indstillinger{dirty ? ' · ugemt' : ''}
                  </button>
                </div>
                {tab === 'settings' ? (
                  <BehaviourSettingsForm
                    detail={detail}
                    settings={draft}
                    disabled={saving}
                    onChange={setDraft}
                  />
                ) : (
                  <>
                    {dirty && !shownPreview && (
                      <p className="anomaly-warning">
                        Indstillingerne er ændret. Statistikken viser de gemte værdier, indtil du
                        beregner forhåndsvisningen.
                      </p>
                    )}
                    <Statistics
                      key={selected}
                      detail={shownPreview ?? detail}
                      preview={!!shownPreview}
                    />
                  </>
                )}
                <div className="behaviour-actions">
                  {detail.plant.deviceFamily === 'GSM' && (
                    <button
                      className="anomaly-button"
                      type="button"
                      disabled={saving || !detail.dataAt}
                      onClick={() => void calculate()}
                    >
                      Vis effekt på historik
                    </button>
                  )}
                  <button
                    className="anomaly-button primary"
                    type="button"
                    disabled={saving || !dirty}
                    onClick={() => void save()}
                  >
                    {saving ? 'Arbejder…' : 'Gem forventninger'}
                  </button>
                  <span className="anomaly-muted">
                    {dirty
                      ? 'Ugemte indstillinger'
                      : detail.profile.version === 0
                      ? 'Bruger standarder'
                      : `Gemt version ${detail.profile.version}`}
                  </span>
                </div>
              </>
            )
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
